"use client";

import { cn } from "@heroui/react";
import NextLink from "next/link";
import { usePathname } from "next/navigation";

import { DOCS_NAV } from "./nav";

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = usePathname();

  return (
    <nav className="flex flex-col gap-6">
      {DOCS_NAV.map((group) => (
        <div key={group.label} className="flex flex-col gap-1">
          <p className="text-muted px-2 text-xs font-semibold tracking-wide uppercase">
            {group.label}
          </p>
          {group.items.map((item) => {
            const active = pathname === item.href;
            return (
              <NextLink
                key={item.href}
                href={item.href}
                onClick={onNavigate}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "rounded-md px-2 py-1.5 text-sm transition-colors",
                  active
                    ? "bg-accent/10 text-accent font-medium"
                    : "text-muted hover:text-foreground",
                )}
              >
                {item.title}
              </NextLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/**
 * Docs navigation: a sticky rail on wide screens, a native `<details>`
 * disclosure on small ones — no drawer component needed.
 */
export function DocsSidebar() {
  return (
    <>
      {/* `self-start` is load-bearing: without it the flex row stretches this
          box to the full column height, and a sticky element can only travel
          inside its own containing block — so a too-tall box unsticks a screen
          early and the rail scrolls away well before the footer. Sized to its
          content instead, it stays pinned for the whole article. */}
      <aside className="sticky top-20 hidden max-h-[calc(100dvh-6rem)] w-56 shrink-0 self-start overflow-y-auto lg:block">
        <NavLinks />
      </aside>

      <details className="border-default/50 bg-surface group rounded-xl border lg:hidden">
        <summary className="text-muted flex cursor-pointer items-center justify-between px-4 py-3 text-sm font-medium select-none">
          Documentation menu
          <span className="transition-transform group-open:rotate-180" aria-hidden>
            ▾
          </span>
        </summary>
        <div className="border-default/50 border-t p-4">
          <NavLinks />
        </div>
      </details>
    </>
  );
}
